import { ErrorNotification } from '@/store/modules/notifications/notifications';
import axios from 'axios';
import { sortScales, estimateMaxSubjects } from '@/utils/helpers';
import { actions, mutations, state as stateTypes } from './types';

export default {
  async [actions.FETCH_DATASET]({ commit, dispatch }, datasetId) {
    try {
      // Get dataset and its metadata
      const { data } = await axios.get(
        `/api/studies/${datasetId}?include=project&include=num_subjects`
      );

      // Get subject variables
      const { data: subject_data } = await axios.get(
        `/api/studies/${datasetId}/subjects/attributes`
      );
      let subject_variables = subject_data.subject_attributes;
      subject_variables.forEach(v => {
        v.type = 'subject';
        v.label = v.scale;
      });

      // Get observation variables
      const { data: observation_data } = await axios.get(
        `/api/studies/${datasetId}/variables`
      );
      let observation_variables = observation_data.variables;
      observation_variables.forEach(v => {
        v.type = 'observation';
        v.label = v.scale;
      });

      // Get variable data
      const { data: var_data } = await axios.post(
        `/api/studies/${datasetId}/variable_data`,
        {}
      );
      let variable_data = var_data.data;

      commit(mutations.SET_DATASET, data.study);
      commit(mutations.SET_SUBJECT_VARIABLES, sortScales(subject_variables));
      commit(
        mutations.SET_OBSERVATION_VARIABLES,
        sortScales(observation_variables)
      );

      let variables = [...subject_variables, ...observation_variables];
      commit(mutations.SET_VARIABLES, sortScales(variables));
      commit(mutations.SET_SELECTED_VARIABLES, sortScales(variables));
      commit(mutations.SET_VARIABLE_DATA, variable_data);
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    }
  },
  /**
   * Fetch a single collection.
   * @param {Object} context
   */
  async [actions.FETCH_COLLECTION]({ commit, dispatch }, collectionId) {
    try {
      // Get collection with metadata
      const { data } = await axios.get(
        `/api/collections/${collectionId}?include=cohorts&include=variables&include=studies&include=num_subjects`
      );
      var c = data.collection;

      // Get first & last visits and visit variable
      var firstVisits = {};
      var lastVisits = {};
      var visitVar = null;
      c.observation_variables.forEach(ov => {
        if (ov['first_visit_num']) {
          visitVar = 'Visit Number';
          firstVisits[ov['ontology']['id']] = ov['first_visit_num'];
          lastVisits[ov['ontology']['id']] = ov['last_visit_num'];
        } else {
          visitVar = 'Visit Event';
          firstVisits[ov['ontology']['id']] = ov['first_visit_event'];
          lastVisits[ov['ontology']['id']] = ov['last_visit_event'];
        }
      });
      commit(mutations.SET_VISIT_VARIABLE, visitVar);
      commit(mutations.SET_FIRST_VISITS, firstVisits);
      commit(mutations.SET_LAST_VISITS, lastVisits);

      // Set other metadata
      c.date_generated_epoch = new Date(c.date_generated).getTime();
      c.has_visits_set = true;
      c.longitudinal = c.studies[0].study.longitudinal;
      // Check if visits are set for all observation variables
      c.observation_variables.forEach(v => {
        if (
          (v.first_visit_event == null && v.first_visit_num == null) ||
          (v.last_visit_event == null && v.last_visit_num == null)
        ) {
          c.has_visits_set = false;
        }
      });

      // Prepare subject & observation variables
      let subject_variables = c.subject_variables.map(v => v.ontology);
      let observation_variables = c.observation_variables;
      observation_variables.forEach(v => {
        v.ontology.first_visit_event = v.first_visit_event;
        v.ontology.first_visit_num = v.first_visit_num;
        v.ontology.last_visit_event = v.last_visit_event;
        v.ontology.last_visit_num = v.last_visit_num;
      });
      observation_variables = observation_variables.map(v => v.ontology);

      // Get variable data
      var variable_data = {};
      if (c.has_visits_set) {
        const { data } = await axios.post(
          `/api/collections/${collectionId}/variable_data`,
          {}
        );
        variable_data = data.data;
      } else {
        // If visits are not set, estimate number of subjects in collection
        var study_ids = c.studies.map(s => s.study_id);
        const { data: visit_data } = await axios.get(
          `/api/studies/subject_variable_visits?id=${study_ids.join('&id=')}`
        );
        var svars = visit_data['visits'];
        var ems = estimateMaxSubjects(svars, observation_variables, 'event');

        c['num_subjects'] = ems['counts']['all'];
        var subject_ids = ems['subjects']['all'];

        const { data } = await axios.post(
          `/api/collections/${collectionId}/variable_data`,
          { subject_ids: subject_ids }
        );
        variable_data = data.data;
      }

      commit(mutations.SET_COLLECTION, c);
      commit(mutations.SET_SUBJECT_VARIABLES, sortScales(subject_variables));
      commit(
        mutations.SET_OBSERVATION_VARIABLES,
        sortScales(observation_variables)
      );

      let variables = [...subject_variables, ...observation_variables];
      commit(mutations.SET_VARIABLES, sortScales(variables));
      commit(mutations.SET_SELECTED_VARIABLES, sortScales(variables));
      commit(mutations.SET_VARIABLE_DATA, variable_data);
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    }
  },
  async [actions.GET_VARIABLE_COUNTS]({ dispatch, state }) {
    const { id: datasetId, longitudinal } = state[stateTypes.DATASET];
    var svc = { subject: {}, observation: {} };
    var nvisits = longitudinal ? 2 : 1;
    var svars = {};

    try {
      const { data } = await axios.get(
        `/api/studies/subject_variable_visits?id=${datasetId}`
      );
      svars = data['visits']['subjects'];

      const subj_ids = Object.keys(svars);
      subj_ids.forEach(subj_id => {
        var study_ids = Object.keys(svars[subj_id]);
        study_ids.forEach(study_id => {
          var var_types = Object.keys(svars[subj_id][study_id]);
          var_types.forEach(var_type => {
            var var_ids = Object.keys(svars[subj_id][study_id][var_type]);
            var_ids.forEach(var_id => {
              var key = var_id + ':' + study_id;
              var add_one = true;

              if (
                typeof svars[subj_id][study_id][var_type][var_id] != 'number'
              ) {
                var evtStr =
                  svars[subj_id][study_id][var_type][var_id]['event'];
                var nEvt = evtStr.split('1').length - 1;
                var numStr = svars[subj_id][study_id][var_type][var_id]['num'];
                var nNum = numStr.split('1').length - 1;
                if (nEvt < nvisits && nNum < nvisits) add_one = false;
              }

              if (add_one) {
                if (!(key in svc[var_type])) {
                  svc[var_type][key] = 0;
                }
                svc[var_type][key] = svc[var_type][key] + 1;
              }
            });
          });
        });
      });
      return svc;
    } catch (err) {
      const notification = new ErrorNotification(err);
      dispatch(notification.dispatch, notification, { root: true });
    }
  },
  [actions.SET_VISIT_VARIABLE]({ commit }, newVisitVar) {
    commit(mutations.SET_FIRST_VISITS, {});
    commit(mutations.SET_LAST_VISITS, {});
    commit(mutations.SET_VISIT_VARIABLE, newVisitVar);
  },
  [actions.SET_FIRST_VISITS]({ commit }, newFirstVisits) {
    commit(mutations.SET_FIRST_VISITS, newFirstVisits);
  },
  [actions.SET_LAST_VISITS]({ commit }, newLastVisits) {
    commit(mutations.SET_LAST_VISITS, newLastVisits);
  },
  // set all variables to same first/last visit
  [actions.SET_FIRST_VISIT]({ commit, state }, newFirstVisit) {
    // Only compute for selected variables
    var selectedVariables = state[stateTypes.SELECTED_VARIABLES];
    var observationVariables = selectedVariables.filter(
      svar => svar.type == 'observation'
    );
    var firstVisits = {};
    observationVariables.forEach(v => {
      firstVisits[v.id] = newFirstVisit;
    });
    commit(mutations.SET_FIRST_VISITS, firstVisits);
  },
  [actions.SET_LAST_VISIT]({ commit, state }, newLastVisit) {
    // Only compute for selected variables
    var selectedVariables = state[stateTypes.SELECTED_VARIABLES];
    var observationVariables = selectedVariables.filter(
      svar => svar.type == 'observation'
    );
    var lastVisits = {};
    observationVariables.forEach(v => {
      lastVisits[v.id] = newLastVisit;
    });
    commit(mutations.SET_LAST_VISITS, lastVisits);
  },
  async [actions.FETCH_VISIT_SUMMARIES]({ commit, dispatch, state }) {
    const is_collection = state[stateTypes.IS_COLLECTION];

    if (is_collection) {
      const { id: collectionId } = state[stateTypes.COLLECTION];
      try {
        const { data } = await axios.get(
          `/api/collections/obs_summaries/${collectionId}`
        );
        commit(mutations.SET_VISIT_SUMMARIES, data['summaries']);
      } catch (err) {
        const notification = new ErrorNotification(err);
        dispatch(notification.dispatch, notification, { root: true });
      }
    } else {
      const { id: datasetId } = state[stateTypes.DATASET];
      try {
        const { data } = await axios.get(
          `/api/studies/obs_summaries/${datasetId}`
        );
        commit(mutations.SET_VISIT_SUMMARIES, data['summaries']);
      } catch (err) {
        const notification = new ErrorNotification(err);
        dispatch(notification.dispatch, notification, { root: true });
      }
    }
  },
  async [actions.FETCH_TIMES_BETWEEN_VISITS]({ commit, dispatch, state }) {
    const is_collection = state[stateTypes.IS_COLLECTION];
    var api_route = is_collection ? 'collections' : 'studies';
    var dataset = is_collection
      ? state[stateTypes.COLLECTION]
      : state[stateTypes.DATASET];
    var visitVariable = state[stateTypes.VISIT_VARIABLE];
    var firstVisits = state[stateTypes.FIRST_VISITS];
    var lastVisits = state[stateTypes.LAST_VISITS];
    // Only compute for selected variables
    var selectedVariables = state[stateTypes.SELECTED_VARIABLES];
    var observationVariables = selectedVariables.filter(
      svar => svar.type == 'observation'
    );

    if (dataset && visitVariable && firstVisits && lastVisits) {
      var query_by =
        visitVariable === 'Visit Event' ? 'visit_event' : 'visit_num';
      var request_url = `/api/${api_route}/time_between_visits/${dataset.id}`;

      var first_visits_l = [];
      var last_visits_l = [];
      var obs_var_ids_l = [];

      // comma-separated lists for first_visits, last_visits, obs_var_ids
      observationVariables
        .map(v => v.id)
        .forEach(o => {
          obs_var_ids_l.push(o);
          first_visits_l.push(firstVisits[o]);
          last_visits_l.push(lastVisits[o]);
        });

      let request_payload = {
        query_by: query_by,
        obs_var_ids: obs_var_ids_l,
        first_visits: first_visits_l,
        last_visits: last_visits_l,
      };

      var cb = async function(reqnum) {
        setTimeout(async function() {
          if (state[stateTypes.TIMES_BETWEEN_VISITS_REQUEST_NUM] > reqnum) {
            //            console.log("reqnum " + reqnum + " is no longer current, skipping request");
            return;
          }

          try {
            const { data } = await axios.post(request_url, request_payload);

            if (state[stateTypes.TIMES_BETWEEN_VISITS_REQUEST_NUM] > reqnum) {
              // console.log("request " + reqnum + " is no longer current, ignoring return value");
              return;
            }

            if (data['query_by'] === query_by) {
              commit(mutations.SET_TIMES_BETWEEN_VISITS, data['times']);
              var ns = 0;
              data['times'].forEach(t => {
                if (t['is_first']) {
                  ns += t.n_subjects;
                }
              });
              commit(mutations.SET_NUM_SELECTED_SUBJECTS, ns);
            }
          } catch ({ response }) {
            const notification = new ErrorNotification(response.data.error);
            dispatch(notification.dispatch, notification, { root: true });
          }
        }, state[stateTypes.TIMES_BETWEEN_VISITS_REQUEST_DELAY_SECS] * 1000);
      };

      commit(mutations.INCREMENT_TIMES_BETWEEN_VISITS_REQUEST_NUM, {
        callback: cb,
      });
    }
  },
  async [actions.GET_STUDY_SUBJECT_VARIABLE_VISITS]({ dispatch, state }) {
    const is_collection = state[stateTypes.IS_COLLECTION];

    if (!is_collection) {
      const { id: datasetId } = state[stateTypes.DATASET];
      try {
        const { data } = await axios.get(
          `/api/studies/subject_variable_visits?id=${datasetId}`
        );
        return data['visits'];
      } catch (err) {
        const notification = new ErrorNotification(err);
        dispatch(notification.dispatch, notification, { root: true });
      }
    } else {
      return null;
    }
  },
  [actions.SET_SELECTED_VARIABLES]({ commit }, svars) {
    commit(mutations.SET_SELECTED_VARIABLES, svars);
  },
  [actions.SET_USE_LONG_SCALE_NAMES]({ commit }, useLong) {
    commit(mutations.SET_USE_LONG_SCALE_NAMES, useLong);
  },
  [actions.SET_ENABLE_VARIABLE_SELECTION]({ commit }, flag) {
    commit(mutations.SET_ENABLE_VARIABLE_SELECTION, flag);
  },
  [actions.SET_IS_COLLECTION]({ commit }, is_collection) {
    commit(mutations.SET_IS_COLLECTION, is_collection);
  },
  [actions.RESET_ALL]({ commit }) {
    commit(mutations.SET_IS_COLLECTION, false);
    commit(mutations.SET_DATASET, null);
    commit(mutations.SET_COLLECTION, null);
    commit(mutations.SET_SUBJECTS, []);
    commit(mutations.SET_VARIABLES, []);
    commit(mutations.SET_SUBJECT_VARIABLES, []);
    commit(mutations.SET_OBSERVATION_VARIABLES, []);
    commit(mutations.SET_SELECTED_VARIABLES, []);
    commit(mutations.SET_VARIABLE_DATA, {});
    commit(mutations.SET_USE_LONG_SCALE_NAMES, false);
    commit(mutations.SET_FIRST_VISITS, {});
    commit(mutations.SET_LAST_VISITS, {});
    commit(mutations.SET_VISIT_SUMMARIES, null);
    commit(mutations.SET_TIMES_BETWEEN_VISITS, []);
    commit(mutations.RESET_TIMES_BETWEEN_VISITS_REQUEST_NUM);
  },
};
